<template>
	<HomeHeroSlider @activeItemChanged="handleActiveItemChanged" @heroLoaded="prepareRowSliders" />
	<div class="main-content">
		<HomeHeroInfoBox :item="activeItem" v-if="activeItem" />
		<rowSlider
			v-for="(category, key) in categories"
			:key="key"
			:category="category.drupal_internal__tid"
			:preview="category.field_movies_preview"
			:dataLoaded="dataLoaded"
			:ref="(el) => setSliderRef(el, category.drupal_internal__tid)"
		></rowSlider>
		<!-- <pulse-loader v-else :loading="true" :color="'white'" :size="'12px'"></pulse-loader> -->
	</div>
	<TeaserPreview />
</template>

<script lang="ts" setup>
	import { computed, ref, onMounted, watch } from "vue"
	import { useDataSyncher } from "../composables/useObserverSyncher"
	import rowSlider from "../components/rowSlider.vue"
	import { useTaxonomyStore } from "../stores/taxonomies"
	import { useTitle } from "@vueuse/core"
	import TeaserPreview from "../components/video/TeaserPreview.vue"
	import { useTeaserStore } from "../stores/teaser"
	import { useIntersectionObserver } from "@vueuse/core"
	import HomeHeroSlider from "../components/home/HomeHeroSlider.vue"
	import { useRoute } from "vue-router"
	import { useMovieStore } from "../stores/movies"
	import { useSeriesStore } from "../stores/series"
	import api from "../composables/useApi"
	import { isLoggedIn } from "../composables/useAuth"
	import HomeHeroInfoBox from "../components/home/HomeHeroInfoBox.vue"
	import { useUserStore } from "../stores/user"

	useTitle("Αρχική | Valor Movies")

	const userStore = useUserStore()
	const activeItem = ref<any>(null)
	const handleActiveItemChanged = (item: any) => {
		activeItem.value = item
	}

	const route = useRoute()
	const {
		getCombinedTypeResults,
		getSingleTypeResults,
		getCombinedContinueWatchingTypeResults,
		getFavouriteResults,
		getSingleContinueWatchingTypeResults,
	} = useDataSyncher()

	const teaserStore = useTeaserStore()
	const movieStore = useMovieStore()
	const seriesStore = useSeriesStore()
	const showCategories = ref(false)
	const taxonomyStore = useTaxonomyStore()
	const loadedCategories = ref<Set<number>>(new Set())
	const sliderRefs = new Map<number, Element>()
	const stopFunctions = new Map<number, () => void>()
	const dataLoaded = ref(false)
	// Fetch data for hero section and taxonomies before component is mounted.
	taxonomyStore.getCategories()

	/**
	 * A computed property to get all categories from taxonomy store.
	 * To make sure the user-based category (ID 44) is only shown to logged-in users
	 * we add an extra check to make sure the full list of taxonomies is only returned when appropriate.
	 */
	const categories = computed(() => {
		if (userStore.state.selected_profile.id !== undefined && isLoggedIn()) {
			return taxonomyStore.state.categories
		}
		return taxonomyStore.state.categories.filter((category) => category.drupal_internal__tid !== 44)
	})

	/**
	 * Check if category data already exists in the store
	 */
	const categoryExistsInStore = (tid: number): boolean => {
		if (route.name === "MoviesPage") {
			return movieStore.state.movies.some((item: any) => item.category === tid)
		} else if (route.name === "SeriesPage") {
			return seriesStore.state.series.some((item: any) => item.category === tid)
		} else {
			// Home route - check both stores
			const hasMovies = movieStore.state.movies.some((item: any) => item.category === tid)
			const hasSeries = seriesStore.state.series.some((item: any) => item.category === tid)
			return hasMovies && hasSeries
		}
	}

	/**
	 * Sets slider ref and initializes intersection observer for lazy loading.
	 * @param el
	 * @param tid
	 */
	const setSliderRef = (el: any | null, tid: number) => {
		if ((tid === 44 || tid === 45) && !isLoggedIn()) return
		if (el?.$el instanceof Element && !sliderRefs.has(tid)) {
			const element = el.$el
			sliderRefs.set(tid, element)
			const { stop } = useIntersectionObserver(
				element,
				async ([{ isIntersecting }]) => {
					if (isIntersecting && tid === 44 && isLoggedIn() && userStore.state.selected_profile.id !== undefined) {
						if (route.name === "MoviesPage") {
							await getSingleContinueWatchingTypeResults(tid, "movie")
						} else if (route.name === "SeriesPage") {
							await getSingleContinueWatchingTypeResults(tid, "series")
						} else {
							await getCombinedContinueWatchingTypeResults(tid)
						}
					} else if (isIntersecting && tid === 45 && isLoggedIn()) {
						if (route.name === "MoviesPage") {
							await getFavouriteResults("movie")
						} else if (route.name === "SeriesPage") {
							await getFavouriteResults("series")
						} else {
							await getFavouriteResults("all")
						}
					} else if (isIntersecting && !loadedCategories.value.has(tid)) {
						loadedCategories.value.add(tid)
						// Only fetch if data doesn't exist in store
						if (!categoryExistsInStore(tid)) {
							// Use route-based fetching
							if (route.name === "MoviesPage") {
								await getSingleTypeResults(tid, "movie")
							} else if (route.name === "SeriesPage") {
								await getSingleTypeResults(tid, "series")
							} else {
								await getCombinedTypeResults(tid)
							}
						}

						dataLoaded.value = true
						// Stop observing after loading
						stop()
						stopFunctions.delete(tid)
					}
				},
				{ threshold: 0.4, rootMargin: "80px" }
			)

			stopFunctions.set(tid, stop)
		}
	}

	onMounted(() => {
		teaserStore.setSelectedItem(undefined)
	})

	const prepareRowSliders = () => {
		// setTimeout(() => {
		showCategories.value = true
		// }, 500)
	}
</script>

<style lang="scss" scoped>
	.v-spinner {
		margin-top: 30px;
		text-align: center;
	}
	.main-content {
		// margin-top: -260px;
		margin-top: -26vw;
		z-index: 9;
		position: relative;
		opacity: 1;

		@include br.respond-to(br.$laptop-large) {
			margin-top: -30vw;
		}

		@include br.respond-to(br.$tablet-small) {
			margin-top: -60px;
		}
	}
</style>
